import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import { useRef, useEffect, useState, useCallback, type JSX } from 'react';
import { T, TPlainTextKey } from '../../../../../../Translations';
import { EmptyState } from './EmptyState';
import { ValidationRules } from '../utils/ValidationRules';

interface InlineTextFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: Maybe<string>;
  emptyStateText: Maybe<TPlainTextKey>;
  maxLength: Maybe<number>;
  showMaxLengthError: Maybe<boolean>;
  required: Maybe<boolean>;
  showRequiredError: Maybe<boolean>;
  inputAriaLabel: Maybe<string>;
  editIntercomTarget: Maybe<string>;
  emptyStateIntercomTarget: Maybe<string>;
  className?: string;
  inputClassName?: string;
}

const DEFAULT_MAX_LENGTH = 255;

function InlineTextField({
  value,
  onChange,
  placeholder,
  emptyStateText,
  maxLength,
  showMaxLengthError,
  required,
  showRequiredError,
  inputAriaLabel,
  editIntercomTarget,
  emptyStateIntercomTarget,
  className = '',
  inputClassName = '',
  style,
}: InlineTextFieldProps & { style?: React.CSSProperties }): JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    setDraftValue(value);
  }

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = useCallback(() => {
    const trimmedValue = draftValue.trim();
    if (trimmedValue !== value) {
      onChange(trimmedValue);
    }
    setIsEditing(false);
  }, [draftValue, value, onChange]);

  const handleCancel = useCallback(() => {
    setDraftValue(value);
    setIsEditing(false);
  }, [value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      }
    },
    [handleSave, handleCancel],
  );

  const handleBlur = (e: React.FocusEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget?.closest('[data-inline-actions]')) {
      return;
    }
    handleSave();
  };

  const maxLengthValue = maxLength.getOrElse(() => DEFAULT_MAX_LENGTH);
  const isRequired = required.getOrElse(() => false);
  const showMaxLen = showMaxLengthError.getOrElse(() => false);
  const showRequired = showRequiredError.getOrElse(() => false);
  const placeholderValue = placeholder.cata({ Just: (p) => p, Nothing: () => undefined });
  const ariaLabel = inputAriaLabel.cata({
    Just: (v) => v,
    Nothing: () => placeholderValue,
  });

  const isEmpty = ValidationRules.isEmpty(draftValue);
  const exceedsMaxLength = ValidationRules.exceedsMaxLength(draftValue, maxLengthValue);

  if (isEmpty && !isEditing && emptyStateText.isJust()) {
    return (
      <EmptyState
        text={emptyStateText.getOrElse(() => 'Add an internal name')}
        onClick={() => {
          setIsEditing(true);
          setDraftValue(value);
        }}
        intercomTarget={emptyStateIntercomTarget}
      />
    );
  }

  if (!isEditing) {
    return (
      <div>
        <div
          onClick={() => {
            setIsEditing(true);
            setDraftValue(value);
          }}
          className="cursor-pointer rounded px-2 py-1.5 -mx-2 -my-1.5 transition-colors break-words"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(234, 234, 235, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <span style={style} className="break-words">
            {value ||
              placeholder.cata({
                Just: (p) => <span className="text-muted-foreground">{p}</span>,
                Nothing: () => null,
              })}
          </span>
        </div>
        {isRequired && showRequired && isEmpty && (
          <div role="alert" className="mt-1 text-sm text-destructive">
            <T kind="This field is required" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={draftValue}
        onChange={(e) => setDraftValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        maxLength={maxLengthValue}
        className={`w-full px-2 py-1.5 rounded focus:outline-none ${inputClassName}`}
        style={{
          ...style,
          backgroundColor: '#ffffff',
          border: '2px solid #187a90',
        }}
        placeholder={placeholderValue}
        aria-label={ariaLabel}
        aria-required={isRequired}
        data-intercom-target={editIntercomTarget.cata({ Just: (t) => t, Nothing: () => undefined })}
      />

      <div
        data-inline-actions
        className="absolute left-0 top-full mt-2 z-10 bg-card border border-border rounded px-3 py-2 flex items-center gap-2"
        style={{ boxShadow: 'var(--elevation-sm)' }}
      >
        <button
          onClick={handleSave}
          className="px-3 py-1.5 bg-accent text-accent-foreground rounded transition-all hover:opacity-90"
          style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
        >
          <T kind="Save" />
        </button>
        <button
          onClick={handleCancel}
          className="px-3 py-1.5 bg-background text-foreground border border-border rounded transition-all hover:bg-muted"
          style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
        >
          <T kind="Cancel" />
        </button>
      </div>

      {showMaxLen && exceedsMaxLength && (
        <div role="alert" className="mt-14 text-sm text-destructive">
          <T kind="May not exceed {{maxLength}} characters" maxLength={maxLengthValue} />
        </div>
      )}
    </div>
  );
}

export default observer(InlineTextField);
